import { walkOf, type Walk } from "@/lib/intake-steps";
import type { IntakeState, IntakeStep } from "@/lib/queries-intake";

/**
 * What the readback panel shows, decided apart from how it draws it (ACL-477).
 *
 * The panel's hard parts are not its markup: they are which of several empty states a reader is
 * in, and whether an answer that is no longer on the route still gets listed. Both are rules a
 * reviewer should be able to read as assertions rather than check by clicking through a contact
 * who happens to be in the right state, so they live here, pure, with
 * `test/intake-readback.test.ts` on them.
 */

/** Which of the panel's mutually exclusive states a reading puts the reader in. */
export type ReadbackKind =
  /** The conversation names no contact, so there is no intake to read. */
  | "no-record"
  /** The tenant has published no flow: not an error, and not the same as an empty run. */
  | "no-flow"
  /** There is a flow, but nothing has been recorded against this contact. */
  | "nothing-answered"
  /** There is something to show. */
  | "answers";

export interface Readback {
  kind: ReadbackKind;
  walk: Walk;
  /** Answers the route no longer passes through, as steps that can be drawn. */
  offPath: IntakeStep[];
  /** The pinned version, for the badge. Null when there is no flow. */
  version: { title: string; version: number } | null;
  /** A newer version of the same tree has been published since this run started. */
  outdated: boolean;
  /** `completed`, `rejected`, `in_progress`, or null when nobody has started. */
  status: IntakeState["status"];
}

const NOTHING: Readback = {
  kind: "no-record",
  walk: walkOf(null),
  offPath: [],
  version: null,
  outdated: false,
  status: null,
};

/**
 * What to show for one reading of a lead's intake.
 *
 * `leadId` null is its own answer rather than a missing-data case: a conversation with no contact
 * record behind it (an unmatched inbound, say) has no intake by definition, and saying so is not
 * the same as saying nothing was asked.
 *
 * An off-route answer counts towards having something to show. A lead whose every answer was
 * abandoned when an earlier one changed has an empty route and a full history, and "nothing
 * answered yet" would be a lie to the one reader most likely to be looking.
 */
export function readbackOf(leadId: string | null, state: IntakeState | undefined | null): Readback {
  if (!leadId) return NOTHING;
  if (!state?.flow) return { ...NOTHING, kind: "no-flow" };

  const walk = walkOf(state);
  const offPath = state.off_path ?? [];
  const version = { title: state.flow.title, version: state.flow.version };
  const empty = walk.steps.length === 0 && offPath.length === 0;
  return {
    kind: empty ? "nothing-answered" : "answers",
    walk,
    offPath,
    version,
    outdated: Boolean(state.outdated),
    status: state.status,
  };
}

/** How a run's state reads in the badge. Null when nobody has started it. */
export function statusText(status: IntakeState["status"]): string | null {
  if (status === "completed") return "Completed";
  if (status === "rejected") return "Rejected";
  if (status === "in_progress") return "In progress";
  return null;
}
